import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import { store } from "@core/store"
import { contentsApi } from "../store/contentsApi"
import {
  getAssetLink,
  removeAsset,
  removeContent,
} from "../services/assetLinkCache"
import {
  assetLinkErrorMessage,
  normalizeContentError,
} from "../utils/contentErrors"

export type AssetLinkErrorReason =
  | "asset_not_found"
  | "content_not_found"
  | "load_error"
export type AssetLinkState =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; reason: AssetLinkErrorReason; message: string }

export interface UseAssetLinkResult {
  state: AssetLinkState
  onMediaError: () => void
  retry: () => void
}

interface AssetIds {
  contentId: number
  assetId: number
}
interface LinkCycle extends AssetIds {
  token: string | undefined
  state: AssetLinkState
  retried: boolean
}

export const useAssetToken = () =>
  useSyncExternalStore(store.subscribe, () => store.getState().auth.token)

// Каждый показ пары ID имеет свой цикл; завершение старого запроса безопасно
// для нового показа и не отменяет общий запрос других потребителей.
export const useAssetLink = (args: AssetIds): UseAssetLinkResult => {
  const token = useAssetToken()
  const cycleRef = useRef<LinkCycle | null>(null)
  const [snapshot, setSnapshot] = useState<{
    cycle: LinkCycle | null
    state: AssetLinkState
  }>({ cycle: null, state: { status: "loading" } })
  const isCurrent = useCallback(
    (cycle: LinkCycle) =>
      cycleRef.current === cycle && cycle.token === store.getState().auth.token,
    [],
  )
  const publish = useCallback((cycle: LinkCycle, state: AssetLinkState) => {
    cycle.state = state
    setSnapshot({ cycle, state })
  }, [])

  const fetchLink = useCallback(
    async (cycle: LinkCycle) => {
      publish(cycle, { status: "loading" })
      try {
        const link = await getAssetLink(cycle.contentId, cycle.assetId)
        if (!isCurrent(cycle)) return
        publish(cycle, { status: "ready", url: link.url })
      } catch (error) {
        if (!isCurrent(cycle)) return
        const { status, code } = normalizeContentError(error)
        const reason: AssetLinkErrorReason =
          status === 404 &&
          (code === "asset_not_found" || code === "content_not_found")
            ? code
            : "load_error"
        if (reason !== "load_error") {
          removeContent(cycle.contentId)
          store.dispatch(
            contentsApi.util.invalidateTags([
              { type: "Contents", id: cycle.contentId },
            ]),
          )
        }
        publish(cycle, {
          status: "error",
          reason,
          message: assetLinkErrorMessage(error),
        })
      }
    },
    [isCurrent, publish],
  )

  useEffect(() => {
    const cycle: LinkCycle = {
      contentId: args.contentId,
      assetId: args.assetId,
      token,
      state: { status: "loading" },
      retried: false,
    }
    cycleRef.current = cycle
    void fetchLink(cycle)
    return () => {
      cycleRef.current = null
    }
  }, [args.assetId, args.contentId, token, fetchLink])

  const current = snapshot.cycle
  const matches =
    current?.contentId === args.contentId &&
    current.assetId === args.assetId &&
    current.token === token
  const state: AssetLinkState = matches ? snapshot.state : { status: "loading" }
  const onMediaError = useCallback(() => {
    if (!current || !isCurrent(current) || current.state.status !== "ready")
      return
    if (current.retried) {
      publish(current, {
        status: "error",
        reason: "load_error",
        message: "Не удалось открыть файл (хранилище/CORS/кодек)",
      })
      return
    }
    current.retried = true
    removeAsset(current.contentId, current.assetId)
    void fetchLink(current)
  }, [current, isCurrent, fetchLink, publish])
  const retry = useCallback(() => {
    if (
      !current ||
      !isCurrent(current) ||
      current.state.status !== "error" ||
      current.state.reason !== "load_error"
    )
      return
    current.retried = false
    removeAsset(current.contentId, current.assetId)
    void fetchLink(current)
  }, [current, isCurrent, fetchLink])

  return { state, onMediaError, retry }
}
